/**
 * Sign-in and sign-out shared with the Stock site (cars-for-sale).
 *
 * The two sites keep separate sessions — the portal an httpOnly cookie, Stock
 * a token in its own localStorage — so neither can end the other's directly.
 * They hand over by navigation instead:
 *
 *   sign-in   portal → Stock: `/stock-handoff` mints a one-time code
 *             (POST /api/portal/sso/issue) and opens Stock's /sso-callback.
 *             Stock → portal: Stock's "Sign in" sends `/login?sso_target=
 *             <stock>/sso-callback`; after signing in here the portal does the
 *             same hand-off back.
 *   sign-out  portal → Stock: after the portal logout the browser visits
 *             `<stock>/logout?return_to=<portal>/login`, which clears Stock's
 *             session and comes back. Stock → portal: Stock's logout visits
 *             `/logout?return_to=<stock>/login` here (LogoutPage).
 *
 * Only the paired Stock origin (VITE_STOCK_FRONTEND_URL) is ever followed.
 */
import { getStockFrontendBase } from './featureFlags';

/** In-portal route that hands the session to Stock and opens it. */
export const STOCK_HANDOFF_PATH = '/stock-handoff';

function stockOrigin(): string {
  return new URL(getStockFrontendBase()).origin;
}

function parse(raw: string | null | undefined): URL | null {
  if (!raw) return null;
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

/** True when `raw` is the paired Stock site's /sso-callback (Stock's "Sign in" target). */
export function isStockSsoTarget(raw: string | null | undefined): boolean {
  const url = parse(raw);
  return (
    url !== null &&
    url.origin === stockOrigin() &&
    url.pathname.replace(/\/+$/, '') === '/sso-callback'
  );
}

/** `raw` when it is a page on the paired Stock site, else null. */
export function stockReturnTo(raw: string | null | undefined): string | null {
  const url = parse(raw);
  return url !== null && url.origin === stockOrigin() ? url.toString() : null;
}

/** Where the portal sends the browser after its own logout: Stock's logout, then back to `portalLoginUrl`. */
export function stockLogoutUrl(portalLoginUrl: string): string {
  return `${getStockFrontendBase()}/logout?return_to=${encodeURIComponent(portalLoginUrl)}`;
}
